import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'
import { startOfDay, endOfDay } from 'date-fns'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { closeModal } from 'store/actions/modals'
import { Button, Textarea, Checkbox, Form } from 'components/Forms'
import { DateTimePicker } from 'components/UI'
import { EventSummaryList } from 'components/Schedule'
import { TimeOffSummaryList } from 'components/Requests'
import { TrackAction } from 'services/TrackAction'
import { Alert, Card } from 'ui'

import { t } from 'translations'

const SchedulePublishModal = ({ shiftIds, timeOffIds, scheduleFilter, timeOffFilter, isConfirming, isConfirmingShift }) => {
  const dispatch = useDispatch()
  const { activatePhoneConfirmations } = useSelector(state => state.settings)
  const [queryRange, setQueryRange] = useState(scheduleFilter ? { rangeStart: scheduleFilter.rangeStart, rangeEnd: scheduleFilter.rangeEnd } : null)
  const [events, setEvents] = useState()
  const [timeOffs, setTimeOffs] = useState()
  const [errorLoading, setErrorLoading] = useState(false)

  const type = isConfirming ? 'CONFIRM' : isConfirmingShift ? 'CONFIRM_SHIFT' : 'PUBLISH'

  // Load events
  useEffect(() => {
    const fetchEvents = async () => {
      setEvents()
      if (!scheduleFilter && !shiftIds?.length) return setEvents([])
      const query = typeof scheduleFilter !== 'undefined' ? { ...scheduleFilter, ...queryRange } : { shiftIds }
      const { result, success } = await Api.post('schedule/schedule/query', { ...query, returnScheduleContactEvents: true })
      if (!success) return setErrorLoading(true)
      setEvents(result)
    }
    fetchEvents()
  }, [shiftIds, scheduleFilter, queryRange])

  // Load time off
  useEffect(() => {
    const fetchTimeOff = async () => {
      setTimeOffs()
      if (!timeOffFilter && !timeOffIds?.length) return setTimeOffs([])
      const query = typeof timeOffFilter !== 'undefined' ? { ...timeOffFilter, ...queryRange } : { _ids: timeOffIds }
      const { result, success } = await Api.get('requests/vacation', { params: { ...query, useNewReturn: true, returnScheduleContactEvents: true } })
      if (!success) return setErrorLoading(true)
      setTimeOffs(result)
    }
    fetchTimeOff()
  }, [timeOffIds, timeOffFilter, queryRange])

  // Function to filter out which shifts to confirm based on user selected options
  const getItemsToConfirm = ({ confirm, reconfirm, reconfirmUnconfirmed, confirmTimeOff }) => {
    return {
      eventsToConfirm: events.filter(({ employeeId, published, sce = [] }) => {
        if (!employeeId) return false
        if (!confirm) return !published
        // If not reconfirming, only confirm shifts that have never been confirmed
        if (!reconfirm && !reconfirmUnconfirmed) return sce.length === 0
        if (reconfirmUnconfirmed) return sce.length === 0 || !sce[sce.length - 1].events.confirmed
        return true
      }),
      timeOffsToConfirm: timeOffs.filter(({ sce = [] }) => {
        if (!confirmTimeOff) return false
        // If not reconfirming, only confirm time off that have never been confirmed
        else if (!reconfirm && !reconfirmUnconfirmed) return sce.length === 0
        // Reconfirm only shifts that the employee has not confirmed
        else if (reconfirmUnconfirmed) return sce.length === 0 || !sce[sce.length - 1].events.confirmed
        else return true
      })
    }
  }

  const onSubmit = async ({ confirm, reconfirmUnconfirmed, customMessage, confirmTimeOff }) => {
    const { eventsToConfirm, timeOffsToConfirm } = getItemsToConfirm({ confirm, reconfirm: true, reconfirmUnconfirmed, confirmTimeOff })

    // If the option to confirm is selected by the user, publish and send a confirmation notice to the assigned users
    if (confirm || confirmTimeOff) {
      let body = {
        shiftIds: eventsToConfirm.map(({ _id }) => _id),
        timeOffIds: timeOffsToConfirm.map(({ _id }) => _id),
        options: { confirm, reconfirm: true, reconfirmUnconfirmed, customMessage, type: 'confirmation' }
      }
      // If we are only confirming timeOffs
      if (isConfirming)
        body = {
          timeOffIds: timeOffsToConfirm.map(({ _id }) => _id),
          options: { confirm, reconfirm: true, customMessage, type: 'confirmation' }
        }

      await Api.post('schedule/confirm', body)
      // Track that user has confirmed shifts
      TrackAction('CONFIRM_SHIFTS')
    }
    // If the option to confirm or reconfirm is not selected, only publish the events
    if (!confirm) {
      await Api.put('schedule/publish', { shiftIds: events.map(({ _id }) => _id), publish: true })
      // Track that user has published shifts
      TrackAction('PUBLISH_SHIFTS')
    }

    dispatch(closeModal())
  }
  if ((!events || !timeOffs) && !errorLoading) return <BaseModal hideButtons title="manager.schedule.PUBLISH_MODAL" isLoading />

  return (
    <BaseModal hideButtons title={`manager.schedule.${type}`}>
      {errorLoading ? (
        <>
          <Alert variant="warning" className="mt-2">
            {t(`manager.schedule.${type ? type : `PUBLISH_MODAL`}.ERROR_LOADING`)}
          </Alert>
          <Button text="actions.CLOSE" className="btn btn-outline-secondary mt-2" onClick={() => dispatch(closeModal())} />
        </>
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={{
            confirm: true,
            reconfirm: isConfirmingShift ? true : false,
            reconfirmUnconfirmed: false,
            customMessage: '',
            confirmTimeOff: isConfirming ? true : false
          }}
          onSubmit={values => onSubmit(values)}
        >
          {({ isSubmitting, values }) => {
            const { confirm, confirmTimeOff } = values
            const { eventsToConfirm, timeOffsToConfirm } = getItemsToConfirm({ ...values, reconfirm: true })

            return (
              <Form className="mt-2">
                <Alert variant="warning" className="my-2" noIcon>
                  {eventsToConfirm.length && !isConfirming && !isConfirmingShift ? (
                    <div>
                      <i className={`fas fa-exclamation-triangle mr-2`} />
                      {t(`manager.schedule.${type}.ALERT`)}
                    </div>
                  ) : null}
                  {confirm ? (
                    <div>
                      <i className={`fas fa-exclamation-triangle mr-2`} />
                      {t('manager.schedule.PUBLISH_MODAL.ALERT_CONFIRM_SHIFTS')}
                    </div>
                  ) : null}
                  {confirmTimeOff ? (
                    <div>
                      <i className={`fas fa-exclamation-triangle mr-2`} />
                      {t('manager.schedule.PUBLISH_MODAL.ALERT_CONFIRM_TIMEOFFS')}
                    </div>
                  ) : null}
                </Alert>

                {/* Only show event summary if user has selected events */}
                {eventsToConfirm.length && !isConfirming ? (
                  <div className="mb-2">
                    <EventSummaryList title={`manager.schedule.PUBLISH_MODAL.EVENTS`} events={eventsToConfirm} />
                  </div>
                ) : null}

                {/* Only show time off summary if user has selected time off */}
                {timeOffsToConfirm.length && type !== 'UNPUBLISH' ? (
                  <div className="mb-2">
                    <TimeOffSummaryList title={`manager.schedule.PUBLISH_MODAL.TIMEOFFS`} timeOffs={timeOffsToConfirm} />
                  </div>
                ) : null}

                <Card className="bg-light mt-2">
                  <Card.Body>
                    {/* Range to confirm */}
                    {!shiftIds && !timeOffIds ? (
                      <div className="form-group">
                        <label className="control-label">{t('manager.schedule.PUBLISH_MODAL.RANGE_TO_PUBLISH.LABEL')}</label>
                        <DateTimePicker
                          value={[queryRange.rangeStart, queryRange.rangeEnd]}
                          onChange={([start, end]) => {
                            if (!start || !end) return
                            else setQueryRange({ rangeStart: startOfDay(start), rangeEnd: endOfDay(end) })
                          }}
                          type="range"
                        />
                        <small className="form-text text-muted">{t('manager.schedule.PUBLISH_MODAL.RANGE_TO_PUBLISH.DESCRIPTION')}</small>
                      </div>
                    ) : null}

                    {/* Extra fields for phone confirmations */}
                    {activatePhoneConfirmations ? (
                      <>
                        <Checkbox module="manager.schedule.PUBLISH_MODAL" name="confirm" translation="phoneConfirm" />
                        <Checkbox module="manager.schedule.PUBLISH_MODAL" name="reconfirmUnconfirmed" />
                        <Textarea module="manager.schedule.PUBLISH_MODAL" name="customMessage" autoComplete="false" />
                      </>
                    ) : (
                      <>
                        <Checkbox module="manager.schedule.PUBLISH_MODAL" name="confirm" />
                      </>
                    )}

                    {/* Extra field for time off confirmations */}
                    {timeOffs.length ? (
                      <>
                        <Checkbox module="manager.schedule.PUBLISH_MODAL" name="confirmTimeOff" />
                      </>
                    ) : null}
                  </Card.Body>
                </Card>

                <div className="mt-2">
                  <Button
                    className="btn btn-primary mr-2"
                    type="submit"
                    disabled={!eventsToConfirm.length && !timeOffsToConfirm.length}
                    isSubmitting={isSubmitting}
                    text="SUBMIT"
                  />
                  <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                    {t(`actions.CLOSE`)}
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </BaseModal>
  )
}

export default SchedulePublishModal
